import { type AppId, appPath, platformSubdomain, type SiteId, sites } from '@tnn/core'

/**
 * A page of another app, by its full address on this site: `appLink('accounting', '/payments/new')` →
 * https://os.<domain>/accounting/payments/new, `appLink('portal', '/clock')` → https://os.<domain>/h/clock. The router
 * treats it as leaving the app, so the page loads that app. Also for links that go out on paper or in a message (a job's
 * QR code, an invite).
 */
export function appLink(app: AppId, path = ''): string {
  // Thananan OS's home is `/`, another app's its base (`/tms`)
  return `${window.location.origin}${appPath(app, path)}`
}

/**
 * A page of a site of its own outside Thananan OS (`sites` in @tnn/core), at the subdomain beside this one
 * (os.thananan.co → sign.thananan.co), or its dev server: `path` follows its `/` (a token, `#…`)
 */
export function siteLink(site: SiteId, path = ''): string {
  const { subdomain, devPort } = sites[site]
  const origin = import.meta.env.DEV
    ? `http://localhost:${devPort}`
    : window.location.origin.replace(`//${platformSubdomain}.`, `//${subdomain}.`)
  return `${origin}/${path}`
}

/** A document's signing link, sent to a customer with no account: a page of the signing site (apps/sign) */
export const signLink = (token: string) => siteLink('sign', token)

/**
 * Where signing in goes next (`/login?callbackUrl=…`, or the older `redirect`): a path of Thananan OS or of any app in
 * it (`/tms/waybills?x=1`), or a full address on this same origin, kept as its path. Anything else — another site,
 * `//host`, `javascript:` — and the sign-in page itself go home, so the link cannot send anyone off the system
 */
export function callbackTarget(raw: string | null | undefined, origin: string = window.location.origin): string {
  if (!raw) return '/'
  try {
    const url = new URL(raw, origin)
    if (url.origin !== origin) return '/'
    // `/.//host` reads as the path `//host`, which a browser takes for another site: one leading slash only
    const path = `/${url.pathname.replace(/^[/\\]+/, '')}`
    if (path === '/login') return '/'
    const target = path + url.search + url.hash
    // What the browser will make of it must still be this site
    return new URL(target, origin).origin === origin ? target : '/'
  } catch {
    return '/'
  }
}

/** The one sign-in page, in Thananan OS, coming back to `callback` (a path) afterwards */
export const loginUrl = (callback?: string) =>
  callback && callback !== '/' ? `/login?callbackUrl=${encodeURIComponent(callback)}` : '/login'

/**
 * A link written from an app's own root, under the app's path in Thananan OS's one router (`appPath`): `/waybills` →
 * `/tms/waybills` in TMS, `/` → `/tms`, `/?scan=1` → `/tms?scan=1`; Thananan OS's `/clock` → `/h/clock`, its root pages
 * (`/`, `/chat`) as they are. A path already under it, a full address, `//host` and a relative one stay as they are
 */
export function scopedPath(app: AppId, to: string): string {
  if (!to.startsWith('/') || to.startsWith('//')) return to
  return appPath(app, to)
}
